import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Plan } from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { euro, type Signal, type TenantBilling } from '../../shared/billing';
import { formatNumber } from '../../shared/format';
import { useSession } from '../../shared/session';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Drawer,
  Empty,
  Field,
  Form,
  Input,
  Note,
  PageHead,
  Stats,
  Table,
  TabPanel,
  Tabs,
  Textarea,
  Toggle,
  useAdminToast,
  type TabItem,
} from '../admin-kit';
import { useBilling } from './master-data';

type TabKey = 'overview' | 'upsell' | 'plans';

/**
 * Abonnementen in Master (MST-6): wat elke organisatie betaalt, welke organisaties een gesprek
 * waard zijn, en de pakketten zelf.
 *
 * Er wordt niets gefactureerd en niets geblokkeerd. Dit is het overzicht voor de eigenaar; de
 * rekensom en de signalen staan in `shared/billing.ts`.
 */
export function SubscriptionsPage() {
  const { t } = useTranslation();
  const toast = useAdminToast();
  const billing = useBilling();
  const [tab, setTab] = useState<TabKey>('overview');
  const kansen = billing.rows.filter((row) => row.signals.length > 0);

  const tabs: TabItem<TabKey>[] = [
    { key: 'overview', label: t('master.billing.tabOverview') },
    { key: 'upsell', label: t('master.billing.tabUpsell'), count: kansen.length },
    { key: 'plans', label: t('master.billing.tabPlans'), count: billing.plans.length },
  ];

  return (
    <AdminPage>
      <PageHead title={t('master.billing.title')} sub={t('master.billing.sub')} />
      <Tabs
        label={t('master.billing.title')}
        idPrefix="billing"
        tabs={tabs}
        value={tab}
        onChange={setTab}
      />
      <TabPanel idPrefix="billing" tabKey={tab} className="a-stack">
        {billing.pending ? (
          <LoadingState rows={5} label={t('common.loading')} />
        ) : billing.error ? (
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        ) : tab === 'overview' ? (
          <Overview rows={billing.rows} />
        ) : tab === 'upsell' ? (
          <Upsell rows={kansen} />
        ) : (
          <Plans plans={billing.plans} rows={billing.rows} onToast={toast.show} />
        )}
      </TabPanel>
      {toast.element}
    </AdminPage>
  );
}

/** De kerncijfers: ook op het startscherm (MST-5). */
export function BillingStats({ rows }: { rows: TenantBilling[] }) {
  const { t } = useTranslation();
  const billing = useBilling();
  const sum = billing.totals;
  if (!sum) return null;
  return (
    <Stats
      label={t('master.billing.figures')}
      items={[
        {
          label: t('master.billing.mrr'),
          value: euro(sum.mrrCents),
          sub: t('master.billing.arr', { amount: euro(sum.arrCents) }),
          testId: 'stat-mrr',
        },
        {
          label: t('master.billing.paying'),
          value: formatNumber(sum.paying),
          sub: t('master.billing.average', { amount: euro(sum.averageCents) }),
        },
        {
          label: t('master.billing.trials'),
          value: formatNumber(sum.trials),
          sub: t('master.billing.withoutPlan', { count: sum.withoutPlan }),
        },
        {
          label: t('master.billing.opportunities'),
          value: formatNumber(rows.filter((row) => row.signals.length > 0).length),
          sub: t('master.billing.opportunitiesSub'),
        },
      ]}
    />
  );
}

function Overview({ rows }: { rows: TenantBilling[] }) {
  const { t } = useTranslation();
  const billing = useBilling();
  return (
    <>
      <Card>
        <CardPad>
          <BillingStats rows={rows} />
        </CardPad>
      </Card>

      <Card>
        <CardHead title={t('master.billing.perPlan')} />
        <Table label={t('master.billing.perPlan')}>
          <thead>
            <tr>
              <th>{t('master.billing.plan')}</th>
              <th className="num">{t('master.billing.organisations')}</th>
              <th className="num">{t('master.billing.mrr')}</th>
            </tr>
          </thead>
          <tbody>
            {billing.totals?.perPlan.map((entry) => (
              <tr key={entry.plan.id}>
                <td>
                  <span className="a-name">{entry.plan.name}</span>
                </td>
                <td className="num">{formatNumber(entry.tenants)}</td>
                <td className="num">{euro(entry.mrrCents)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card>
        <CardHead title={t('master.billing.perOrganisation')} count={rows.length} />
        {rows.length === 0 ? (
          <Empty icon={<AdminIcon name="building" size={24} />} title={t('tenants.empty')} />
        ) : (
          <Table label={t('master.billing.perOrganisation')}>
            <thead>
              <tr>
                <th>{t('tenants.name')}</th>
                <th>{t('master.billing.plan')}</th>
                <th>{t('master.billing.status')}</th>
                <th className="num">{t('master.billing.users')}</th>
                <th className="num">{t('master.billing.mrr')}</th>
              </tr>
            </thead>
            <tbody>
              {[...rows]
                .sort((a, b) => b.mrrCents - a.mrrCents)
                .map((row) => (
                  <tr key={row.tenant.id}>
                    <td>
                      <TenantLink row={row} />
                    </td>
                    <td>{row.plan?.name ?? <span className="a-muted">—</span>}</td>
                    <td>
                      <SubscriptionBadge row={row} />
                    </td>
                    <td className="num">
                      <UsersCell row={row} />
                    </td>
                    <td className="num">{euro(row.mrrCents)}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

function Upsell({ rows }: { rows: TenantBilling[] }) {
  const { t } = useTranslation();
  const gesorteerd = [...rows].sort(
    (a, b) => (b.signals[0]?.weight ?? 0) - (a.signals[0]?.weight ?? 0),
  );
  return (
    <Card>
      <CardHead title={t('master.billing.tabUpsell')} count={rows.length} />
      <CardPad>
        <p className="a-muted">{t('master.billing.upsellIntro')}</p>
      </CardPad>
      {gesorteerd.length === 0 ? (
        <Empty icon={<AdminIcon name="sparkle" size={24} />} title={t('master.billing.noUpsell')} />
      ) : (
        <Table label={t('master.billing.tabUpsell')}>
          <thead>
            <tr>
              <th>{t('tenants.name')}</th>
              <th>{t('master.billing.signals')}</th>
              <th>{t('master.billing.plan')}</th>
              <th>{t('master.billing.suggestion')}</th>
            </tr>
          </thead>
          <tbody>
            {gesorteerd.map((row) => (
              <tr key={row.tenant.id} data-testid="upsell-row">
                <td>
                  <TenantLink row={row} />
                </td>
                <td>
                  <ul className="a-stack" style={{ gap: 4 }}>
                    {row.signals.map((signal) => (
                      <li key={signal.kind}>
                        <SignalBadge signal={signal} />{' '}
                        <span className="a-meta">
                          {t(`master.billing.signal.${signal.kind}`, signal.values)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </td>
                <td>{row.plan?.name ?? '—'}</td>
                <td>
                  {row.suggestedPlan ? (
                    <Badge tone="info">{row.suggestedPlan.name}</Badge>
                  ) : (
                    <span className="a-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function Plans({
  plans,
  rows,
  onToast,
}: {
  plans: Plan[];
  rows: TenantBilling[];
  onToast: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [edit, setEdit] = useState<Plan | 'new' | null>(null);
  return (
    <Card>
      <CardHead
        title={t('master.billing.tabPlans')}
        count={plans.length}
        actions={
          <Btn
            variant="sec"
            size="sm"
            icon={<AdminIcon name="plus" size={16} />}
            onClick={() => setEdit('new')}
          >
            {t('master.billing.newPlan')}
          </Btn>
        }
      />
      <CardPad>
        <p className="a-muted">{t('master.billing.plansIntro')}</p>
      </CardPad>
      <Table label={t('master.billing.tabPlans')}>
        <thead>
          <tr>
            <th>{t('master.billing.plan')}</th>
            <th className="num">{t('master.billing.pricePerMonth')}</th>
            <th className="num">{t('master.billing.pricePerUser')}</th>
            <th className="num">{t('master.billing.maxUsers')}</th>
            <th>{t('master.billing.included')}</th>
            <th className="num">{t('master.billing.organisations')}</th>
            <th className="act">
              <span className="a-sr">{t('master.people.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {[...plans]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((plan) => (
              <tr key={plan.id}>
                <td>
                  <span className="a-name">{plan.name}</span>{' '}
                  {plan.active ? null : <Badge>{t('master.billing.inactive')}</Badge>}
                  <div className="a-meta">{plan.description}</div>
                </td>
                <td className="num">{euro(plan.pricePerMonthCents)}</td>
                <td className="num">{euro(plan.pricePerUserCents)}</td>
                <td className="num">
                  {plan.maxUsers === null ? '∞' : formatNumber(plan.maxUsers)}
                </td>
                <td className="a-meta">{plan.includedFlags.length}</td>
                <td className="num">{rows.filter((row) => row.plan?.id === plan.id).length}</td>
                <td className="act">
                  <Btn variant="ghost" size="sm" onClick={() => setEdit(plan)}>
                    {t('master.billing.edit')}
                  </Btn>
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      {edit ? (
        <PlanDrawer
          plan={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={(name) => {
            setEdit(null);
            onToast(t('master.billing.planSaved', { name }));
          }}
        />
      ) : null}
    </Card>
  );
}

/** Een pakket aanmaken of wijzigen. Bedragen in euro op het scherm, in cent naar de API. */
function PlanDrawer({
  plan,
  onClose,
  onSaved,
}: {
  plan: Plan | null;
  onClose: () => void;
  onSaved: (name: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [key, setKey] = useState(plan?.key ?? '');
  const [name, setName] = useState(plan?.name ?? '');
  const [description, setDescription] = useState(plan?.description ?? '');
  const [perMonth, setPerMonth] = useState(plan ? String(plan.pricePerMonthCents / 100) : '0');
  const [perUser, setPerUser] = useState(plan ? String(plan.pricePerUserCents / 100) : '0');
  const [maxUsers, setMaxUsers] = useState(plan?.maxUsers ? String(plan.maxUsers) : '');
  const [flags, setFlags] = useState((plan?.includedFlags ?? []).join(', '));
  const [sortOrder, setSortOrder] = useState(String(plan?.sortOrder ?? 0));
  const [active, setActive] = useState(plan?.active ?? true);

  const cents = (value: string) => Math.round(Number(value.replace(',', '.')) * 100);
  const geldig =
    name.trim().length >= 2 &&
    (plan !== null || /^[a-z0-9-]{2,32}$/.test(key)) &&
    Number.isFinite(cents(perMonth)) &&
    Number.isFinite(cents(perUser)) &&
    // Het contract wil een geheel getal; "12,5" gaf anders een kale 400.
    (maxUsers.trim() === '' || (Number.isInteger(Number(maxUsers)) && Number(maxUsers) > 0));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        description: description.trim(),
        pricePerMonthCents: cents(perMonth),
        pricePerUserCents: cents(perUser),
        maxUsers: maxUsers.trim() === '' ? null : Number(maxUsers),
        includedFlags: flags
          .split(',')
          .map((flag) => flag.trim())
          .filter(Boolean),
        active,
        sortOrder: Number(sortOrder) || 0,
      };
      const { error } = plan
        ? await api.PATCH('/api/v1/platform/plans/{planId}', {
            params: { path: { planId: plan.id } },
            body: body as never,
          })
        : await api.POST('/api/v1/platform/plans', { body: { ...body, key } as never });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
      onSaved(name.trim());
    },
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title={plan ? plan.name : t('master.billing.newPlan')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn disabled={!geldig || save.isPending} onClick={() => save.mutate()}>
            {t('tenants.save')}
          </Btn>
        </>
      }
    >
      <Form onSubmit={() => geldig && save.mutate()}>
        {save.isError ? (
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        ) : null}
        {plan ? null : (
          <Field label={t('master.billing.key')} hint={t('master.billing.keyHint')}>
            {(props) => (
              <Input
                {...props}
                className="a-mono"
                value={key}
                onChange={(event) => setKey(event.target.value.toLowerCase())}
              />
            )}
          </Field>
        )}
        <Field label={t('master.billing.planName')}>
          {(props) => (
            <Input {...props} value={name} onChange={(event) => setName(event.target.value)} />
          )}
        </Field>
        <Field label={t('master.billing.description')} optional>
          {(props) => (
            <Textarea
              {...props}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('master.billing.pricePerMonthEuro')}>
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              value={perMonth}
              onChange={(event) => setPerMonth(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('master.billing.pricePerUserEuro')}>
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              value={perUser}
              onChange={(event) => setPerUser(event.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('master.billing.maxUsers')}
          hint={t('master.billing.maxUsersHint')}
          optional
        >
          {(props) => (
            <Input
              {...props}
              inputMode="numeric"
              value={maxUsers}
              onChange={(event) => setMaxUsers(event.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('master.billing.included')}
          hint={t('master.billing.includedHint')}
          optional
        >
          {(props) => (
            <Textarea
              {...props}
              className="a-mono"
              value={flags}
              onChange={(event) => setFlags(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('master.billing.sortOrder')}>
          {(props) => (
            <Input
              {...props}
              inputMode="numeric"
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
            />
          )}
        </Field>
        <div className="a-switch">
          <div className="a-switch-text">
            <span className="a-switch-title">{t('master.billing.activePlan')}</span>
          </div>
          <Toggle checked={active} onChange={setActive} label={t('master.billing.activePlan')} />
        </div>
      </Form>
    </Drawer>
  );
}

export function TenantLink({ row }: { row: TenantBilling }) {
  return (
    <Link
      to="/master/organisaties/$tenantId"
      params={{ tenantId: row.tenant.id }}
      className="a-name a-link"
    >
      {row.tenant.name}
    </Link>
  );
}

export function SubscriptionBadge({ row }: { row: TenantBilling }) {
  const { t } = useTranslation();
  const status = row.subscription?.status;
  if (!status) return <Badge>{t('master.billing.none')}</Badge>;
  const tone =
    status === 'active'
      ? 'ok'
      : status === 'trial'
        ? 'info'
        : status === 'paused'
          ? 'attention'
          : 'neutral';
  return <Badge tone={tone}>{t(`master.billing.statuses.${status}`)}</Badge>;
}

export function UsersCell({ row }: { row: TenantBilling }) {
  const active = row.stats?.active ?? 0;
  const max = row.plan?.maxUsers;
  return (
    <span className={row.usage !== null && row.usage >= 0.8 ? 'a-name' : undefined}>
      {formatNumber(active)}
      {max ? ` / ${formatNumber(max)}` : ''}
    </span>
  );
}

function SignalBadge({ signal }: { signal: Signal }) {
  const { t } = useTranslation();
  const tone = signal.weight >= 80 ? 'danger' : signal.weight >= 60 ? 'attention' : 'info';
  return <Badge tone={tone}>{t(`master.billing.signalShort.${signal.kind}`)}</Badge>;
}
